import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import boxiaCircleLogo from "../assets/images/Boxia Circle Main.png";
import boxiaTextDarkMode from "../assets/images/Boxia text for dark mode.svg";
import boxiaTextLightMode from "../assets/images/Boxia text for light mode.svg";
// Import integration logos
import googleLogo from "../assets/images/logos/google.svg";
import microsoftLogo from "../assets/images/logos/microsoft.svg";
import jiraLogo from "../assets/images/logos/jira.svg";
import trelloLogo from "../assets/images/logos/trello.svg";
import slackLogo from "../assets/images/logos/slack.svg";
import githubLogo from "../assets/images/logos/github.svg";
import dropboxLogo from "../assets/images/logos/dropbox.svg";
import salesforceLogo from "../assets/images/logos/salesforce.svg";
import asanaLogo from "../assets/images/logos/asana.svg";
import evernoteLogo from "../assets/images/logos/evernote.svg";
import zapierLogo from "../assets/images/logos/zapier.svg";
import hubspotLogo from "../assets/images/logos/hubspot.svg";
import { ArrowRight, Menu } from "lucide-react";

const transition = {
  type: "spring",
  mass: 0.5,
  damping: 11.5,
  stiffness: 100,
  restDelta: 0.001,
  restSpeed: 0.001,
};

const MenuItem = ({ setActive, active, item, children }) => {
  return (
    <div
      onMouseEnter={() => setActive(item)}
      onMouseLeave={() => setActive(null)}
      className="relative"
    >
      <motion.div
        transition={{ duration: 0.3 }}
        className="cursor-pointer group"
      >
        <a
          href={`/${item.toLowerCase()}`}
          className="inline-flex items-center h-10 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-300 transition-colors duration-300"
        >
          {item}
        </a>
      </motion.div>
      {active !== null && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {active === item && (
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 pt-2">
              <motion.div
                transition={transition}
                layoutId="active"
                className="bg-white dark:bg-gray-900 backdrop-blur-lg rounded-xl border-[1px] border-black/5 dark:border-white/5 shadow-lg overflow-hidden"
                initial={{ opacity: 1 }}
              >
                <motion.div
                  layout
                  className="w-max h-full p-3"
                  initial={{ opacity: 1 }}
                >
                  {children}
                </motion.div>
              </motion.div>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

const NavBar = ({ darkMode, logoFade }) => {
  const [active, setActive] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const featuresMenu = [
    {
      icon: "📋",
      name: "Task Management",
      description: "AI-powered task organization",
    },
    { icon: "📝", name: "Note-Taking", description: "Smart, contextual notes" },
    { icon: "📅", name: "Calendar", description: "AI-optimized scheduling" },
    {
      icon: "📁",
      name: "File Storage",
      description: "Unified, intelligent storage",
    },
    {
      icon: "🔗",
      name: "Integrations",
      description: "Connect your favorite tools",
    },
    {
      icon: "🔒",
      name: "Security",
      description: "Advanced encryption & privacy",
    },
  ];

  const pricingMenu = [
    // {
    //   name: "Personal",
    //   price: "Free",
    //   period: "",
    //   description: "For individual users",
    // },
    {
      name: "Pro",
      price: "$9.99",
      period: "/month",
      description: "For power users",
      highlight: true,
    },
    {
      name: "Team",
      price: "$12.99",
      period: "/user/month",
      description: "For small teams",
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For large organizations",
    },
  ];

  const integrationsMenu = [
    {
      icon: googleLogo,
      name: "Google",
      description: "Sync with Google Workspace",
    },
    {
      icon: microsoftLogo,
      name: "Microsoft",
      description: "Connect with Microsoft 365",
    },
    { icon: slackLogo, name: "Slack", description: "Collaborate seamlessly" },
    { icon: trelloLogo, name: "Trello", description: "Manage tasks visually" },
    {
      icon: githubLogo,
      name: "GitHub",
      description: "Integrate your code workflow",
    },
    {
      icon: zapierLogo,
      name: "Zapier",
      description: "Connect with 1000+ apps",
    },
  ];

  const menuVariants = {
    closed: { opacity: 0, x: "100%" },
    open: { opacity: 1, x: 0 },
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 mx-auto mt-4 rounded-xl w-full max-w-7xl">
      <div className="relative">
        {/* Add gradient background with blur */}
        <div className="absolute inset-0 z-[0] rounded-xl bg-white/[0.4] dark:bg-gray-900/[0.4] backdrop-blur-md border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5" />

        <div className="relative z-10 max-w-screen-xl mx-auto px-6">
          {/* Rest of the nav content */}
          <div className="flex items-center justify-between h-16">
            {/* Logo section - update hover effect */}
            <div className="flex items-center">
              <a href="/" className="flex items-center group">
                <img
                  src={boxiaCircleLogo}
                  alt="Boxia Circle Logo"
                  className="w-8 h-8 mr-3 transition-transform duration-300 group-hover:scale-110"
                />
                <img
                  src={darkMode ? boxiaTextDarkMode : boxiaTextLightMode}
                  alt="Boxia Logo"
                  className={`h-8 transition-all duration-300 ${
                    logoFade ? "opacity-0" : "opacity-100"
                  } group-hover:opacity-80`}
                />
              </a>
            </div>

            {/* Menu items - update styling */}
            <div className="flex-grow flex justify-center">
              <div className="hidden md:flex md:items-center md:space-x-8">
                {/* Update MenuItem hover states */}
                <MenuItem
                  className="text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-300 transition-colors duration-300"
                  setActive={setActive}
                  active={active}
                  item="Features"
                >
                  <div className="grid grid-cols-2 gap-4 p-4">
                    {featuresMenu.map((feature) => (
                      <a
                        key={feature.name}
                        href="/features"
                        className={`flex items-center space-x-3 p-2 rounded-lg transition-colors duration-200 ${
                          darkMode
                            ? "hover:bg-gray-700 text-gray-200"
                            : "hover:bg-gray-100 text-gray-800"
                        }`}
                      >
                        <span className="text-2xl">{feature.icon}</span>
                        <div>
                          <p className="font-medium">{feature.name}</p>
                          <p
                            className={`text-sm ${
                              darkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            {feature.description}
                          </p>
                        </div>
                      </a>
                    ))}
                  </div>
                  <div className="p-4">
                    <a
                      href="/features"
                      className={`block text-center py-2 px-5 rounded-xl text-sm font-medium text-white transition-all duration-300 ${
                        darkMode
                          ? "bg-gray-800 hover:bg-gray-700"
                          : "bg-gray-700 hover:bg-gray-800"
                      }`}
                    >
                      View All Features
                    </a>
                  </div>
                </MenuItem>
                <MenuItem
                  className="text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-300 transition-colors duration-300"
                  setActive={setActive}
                  active={active}
                  item="Integrations"
                >
                  <div className="grid grid-cols-2 gap-4 p-4">
                    {integrationsMenu.map((integration) => (
                      <a
                        key={integration.name}
                        href="/integrations"
                        className={`flex items-center space-x-3 p-2 rounded-lg transition-colors duration-200 ${
                          darkMode
                            ? "hover:bg-gray-700 text-gray-200"
                            : "hover:bg-gray-100 text-gray-800"
                        }`}
                      >
                        <img
                          src={integration.icon}
                          alt={`${integration.name} logo`}
                          className="w-6 h-6"
                        />
                        <div>
                          <p className="font-medium">{integration.name}</p>
                          <p
                            className={`text-sm ${
                              darkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            {integration.description}
                          </p>
                        </div>
                      </a>
                    ))}
                  </div>
                  <div className="p-4">
                    <a
                      href="/integrations"
                      className={`block text-center py-2 px-5 rounded-xl text-sm font-medium text-white transition-all duration-300 ${
                        darkMode
                          ? "bg-gray-800 hover:bg-gray-700"
                          : "bg-gray-700 hover:bg-gray-800"
                      }`}
                    >
                      View All Integrations
                    </a>
                  </div>
                </MenuItem>

                {/* Simple link to Teams */}
                <a
                  href="/teams"
                  className="inline-flex items-center h-10 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-300 transition-colors duration-300"
                >
                  Teams
                </a>

                <MenuItem
                  className="text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-300 transition-colors duration-300"
                  setActive={setActive}
                  active={active}
                  item="Pricing"
                >
                  <div className="grid grid-cols-1 gap-4 p-4">
                    {pricingMenu.map((plan) => (
                      <a
                        key={plan.name}
                        href="/pricing"
                        className={`flex flex-col p-3 rounded-lg transition-colors duration-200 ${
                          darkMode
                            ? "hover:bg-gray-700 text-gray-200"
                            : "hover:bg-gray-100 text-gray-800"
                        } ${plan.highlight ? "ring-2 ring-indigo-500" : ""}`}
                      >
                        <span className="font-medium">{plan.name}</span>
                        <span className="text-lg font-bold">
                          {plan.price}
                          <span className="text-sm font-normal">
                            {plan.period}
                          </span>
                        </span>
                        <span
                          className={`text-sm ${
                            darkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          {plan.description}
                        </span>
                      </a>
                    ))}
                  </div>
                  <div className="p-4">
                    <a
                      href="/pricing"
                      className={`block text-center py-2 px-5 rounded-xl text-sm font-medium text-white transition-all duration-300 ${
                        darkMode
                          ? "bg-gray-800 hover:bg-gray-700"
                          : "bg-gray-700 hover:bg-gray-800"
                      }`}
                    >
                      Compare All Plans
                    </a>
                  </div>
                </MenuItem>

                {/* Simple link to Blog */}
                <a
                  href="/blog"
                  className="inline-flex items-center h-10 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-300 transition-colors duration-300"
                >
                  Blog
                </a>

                {/* Simple link to Changelog */}
                <a
                  href="/changelog"
                  className="inline-flex items-center h-10 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-300 transition-colors duration-300"
                >
                  Changelog
                </a>
                {/* <MenuItem
                  className="text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-300 transition-colors duration-300"
                  setActive={setActive}
                  active={active}
                  item="Blog"
                /> */}
              </div>
            </div>

            {/* Primary CTA button - update wording */}
            <a
              href="https://app.boxia.io/auth/signup"
              className="hidden md:inline-flex items-center px-6 py-2.5 text-sm font-medium rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200 text-white shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Sign Up Now
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 duration-300" />
            </a>

            {/* Mobile menu button - update styling */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-300 transition-colors duration-300"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu - update styling */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="md:hidden absolute top-full left-0 right-0 mt-2 mx-4 rounded-xl overflow-hidden border-[1px] border-black/5 dark:border-white/5 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg shadow-lg"
          >
            <div className="px-6 py-4 space-y-3">
              {["Features", "Integrations", "Pricing", "Blog", "Changelog"].map(
                (item) => (
                  <a
                    key={item}
                    href={`/${item.toLowerCase()}`}
                    className={`block px-4 py-3 rounded-2xl text-base font-medium transition-all duration-300 ${
                      darkMode
                        ? "text-gray-200 hover:bg-gray-800 hover:text-white"
                        : "text-gray-800 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item}
                  </a>
                )
              )}
              <a
                href="https://app.boxia.io/auth/signup"
                className={`block px-4 py-3 rounded-2xl text-base font-medium text-white transition-all duration-300 ${
                  darkMode
                    ? "bg-gray-800 hover:bg-gray-700"
                    : "bg-gray-700 hover:bg-gray-600"
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Sign Up Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default NavBar;
